import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { memo } from "react";

import type { ProtocolQuestionnaireIndexStatus } from "@/services/fhir/protocol/hooks/useOrganizationProtocolQuestionnaireIndex";

const MAX_VISIBLE = 2;

export interface QuestionnaireProtocolLinkChipsProps {
  /** Nomes de exibição dos protocolos (já resolvidos pelo hook). */
  protocolNames: string[];
  status: ProtocolQuestionnaireIndexStatus;
  /** Texto curto quando o índice carregou e não há protocolos. */
  emptyLabel?: string;
  /** id opcional para acessibilidade do grupo de chips. */
  ariaLabelledBy?: string;
}

/**
 * Linha visual: vínculo do questionário com protocolos da organização.
 * Mostra chips com limite visível e tooltip no "+N"; estados de carga/erro discretos.
 */
export const QuestionnaireProtocolLinkChips = memo(
  function QuestionnaireProtocolLinkChips({
    protocolNames,
    status,
    emptyLabel = "Não vinculado a protocolos",
    ariaLabelledBy,
  }: QuestionnaireProtocolLinkChipsProps) {
    if (status === "loading" || status === "idle") {
      return (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 0.5 }}
          aria-busy="true"
        >
          <CircularProgress size={12} thickness={5} />
          <Typography variant="caption" color="text.secondary" component="span">
            Protocolos…
          </Typography>
        </Stack>
      );
    }

    if (status === "error") {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5 }}
        >
          Vínculos com protocolos indisponíveis
        </Typography>
      );
    }

    if (protocolNames.length === 0) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5 }}
          id={ariaLabelledBy}
        >
          {emptyLabel}
        </Typography>
      );
    }

    const visible = protocolNames.slice(0, MAX_VISIBLE);
    const hiddenCount = protocolNames.length - visible.length;
    const tooltipTitle = protocolNames.join("\n");

    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.5, gap: 0.5 }}
        id={ariaLabelledBy}
      >
        <AccountTreeOutlinedIcon
          sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
          aria-hidden
        />
        <Typography variant="caption" color="text.secondary" component="span" sx={{ mr: 0.25 }}>
          Protocolos:
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
          {visible.map((name, idx) => (
            <Chip
              key={`${name}-${idx}`}
              label={name}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ height: 22, maxWidth: 200, "& .MuiChip-label": { px: 0.75 } }}
            />
          ))}
          {hiddenCount > 0 ? (
            <Tooltip title={tooltipTitle} arrow placement="top">
              <Chip
                label={`+${hiddenCount}`}
                size="small"
                variant="filled"
                sx={{ height: 22, cursor: "default" }}
              />
            </Tooltip>
          ) : null}
        </Box>
      </Stack>
    );
  },
);

QuestionnaireProtocolLinkChips.displayName = "QuestionnaireProtocolLinkChips";
